/**
 * 경량 토스트 — 자동 소멸 알림 (사이트 공용).
 * 모바일(/m/·/ma/)은 화면 맨하단에서 떠올랐다 스르르 사라지고, 관리자(/adm/)는 좌측 상단,
 * 그 외 사이트는 상단 중앙에서 내려온다.
 * jquery-toast-plugin 의존 제거, 자체 CSS 주입(Tooltip.js 와 동일한 패턴).
 *
 * 사용 —
 *   $j.Ui.Toast.show('저장되었습니다', { type:'success' });   // 권장(메시지 우선)
 *   $j.Ui.Toast.show('success', '저장되었습니다');            // 레거시(아이콘, 메시지) 호환
 *   $j.Ui.Toast.show('<b>선택완료</b>', { html:true, timeout:3000 });
 *
 * opts —
 *   type   : 'default'(기본) | 'success' | 'warning' | 'error' | 'info'
 *            톤(면·테두리·글자 색)과 좌측 아이콘 배지를 함께 정한다.
 *            **안 주면 중립(회색)이다** — 색이 안 뜬다고 고장이 아니라 '성공도 경고도
 *            아님'이다. '…중' 같은 진행 알림이 그 자리다(/adm/dev/erd).
 *            반대로 '저장했습니다'·'복사했습니다' 같은 **완료 알림은 type:'success'** 를 준다.
 *            (예전엔 관리자 토스트가 종류와 무관하게 전부 초록이라 구분 자체가 없었다)
 *   theme  : 'adm' | 'dark' | 'green' | 'yellow'. 미지정 시 사이트별 기본
 *            (Admin=adm — 화면설정 토큰을 타는 중립 판, 그 외(Mobile/Devil)=dark)
 *   bg     : 배경색 직접 지정(테마보다 우선). 예: '#ff5722'
 *   color  : 글자색 직접 지정(테마보다 우선)
 *   place  : 'top' | 'bottom' | 'top-left' | 'center'. 미지정 시 사이트별 기본
 *            (Mobile·MobileAdmin=bottom, Admin=top-left, 그 외(Devil 등)=top)
 *            top-left 는 모달(공용 $j.Ui.Modal)이 열려 있으면 페이지 좌상단이 아니라
 *            **그 모달 헤더 좌측**에 붙는다(자동 전환, place 를 직접 지정하면 안 탄다).
 *            판정은 한 박자 뒤라, 같은 핸들러에서 곧바로 닫는 모달은 '열림'으로 치지 않는다
 *            (버튼으로 닫히는 모달 → 화면 좌상단, 남아 있는 모달 → 모달 안)
 *            bottom 은 화면 맨하단에 떠올랐다가 그 자리에서 스르르 사라지고,
 *            (하단 고정버튼을 가리면 안 되면 opts.offset 으로 띄운다)
 *            top 은 상단에서 슬라이드다운, top-left 는 좌측 상단에 바짝 붙는 작은 알림
 *            (화면 가운데를 가리면 안 되는 관리자 창 등)
 *            center 는 화면 정중앙에서 옅게 확대되며 뜬다(항상 opt-in — 사이트 기본값 아님).
 *            뒤에 있는 내용을 가리므로 짧고 확실히 봐야 하는 알림에만 쓴다.
 *   offset : 화면 가장자리에서 띄울 거리(px). place:'bottom' 은 아래에서, 'top' 은 위에서.
 *            기본 — bottom 24(MobileAdmin 은 하단 네비 64px 위인 76), top 76(헤더 55px 아래)
 *   timeout: 자동 소멸(ms). 기본 1500. 0 이면 자동으로 닫지 않는다(closable 과 함께 쓸 것)
 *   html   : true 면 msg 를 raw HTML 로 렌더(기본 false = 텍스트 이스케이프)
 *   closable: true 면 우측에 닫기(✕) 버튼. 기본 false —
 *            공용 컴포넌트라 기존 호출부(짧게 뜨고 사라지는 알림)의 모양은 그대로 둔다.
 *            오래 띄우는 경고처럼 사용자가 직접 치우고 싶은 알림에만 켠다.
 *
 * 화면 공통 기본값 —
 *   $j.Ui.Toast.defaults({ place:'top' });   // 그 화면의 모든 show() 에 적용
 *   사이트 기본값보다 세고 show(msg, opts) 보다는 약하다. 컴포넌트가 화면을 알 수 없으니
 *   (하단 고정버튼이 토스트에 가리는 등) 다른 자리가 필요한 화면이 페이지 모듈에서 스스로 켠다.
 */
const MODAL_SETTLE_MS = 260;   // 모달 등장(reveal 30ms + 전이 최장 .22s)이 끝날 때까지

export default class Toast {
    constructor() {
        this.$el = null;
        this._timer = null;
        this._styleInjected = false;
        this._defaults = {};   // 화면 공통 기본값 (defaults() 로 설정)
        this._seq = 0;         // 미뤄 둔 렌더가 뒤늦게 덮어쓰지 않게 (show/hide 마다 증가)
    }

    /* ───────────────── public ───────────────── */

    /**
     * @param {string} a - 메시지, 또는 레거시 시그니처의 아이콘(type)
     * @param {(Object|string)} [b] - opts 객체. 문자열이면 레거시 (icon, title) 로 해석
     */
    show = (a, b) => {
        // 레거시 (icon, title) 호환: 두 번째 인자가 문자열이면 (type, msg) 로 간주
        let msg, opts;
        if (typeof b === 'string') { msg = b; opts = { ...this._defaults, type: a }; }
        else { msg = a; opts = { ...this._defaults, ...(b || {}) }; }

        const type = opts.type || 'default';
        const theme = opts.theme || this._siteTheme();
        const placeName = opts.place || this._sitePlace();
        const timeout = opts.timeout === 0 ? 0 : (opts.timeout || 1500);
        const body = opts.html ? msg : this._esc(msg);
        const closeBtn = opts.closable
            ? '<button type="button" class="jb-toast-x" aria-label="닫기">✕</button>' : '';

        clearTimeout(this._timer);
        const seq = ++this._seq;
        const render = () => {
            if (seq !== this._seq) return;   // 그 사이 다음 show()/hide() 가 왔다
            this._render(msg, opts, { type, theme, placeName, timeout, body, closeBtn });
        };
        // top-left 는 모달 안/밖을 **한 박자 뒤에** 가른다. 확인 버튼 핸들러는 대개
        // `Toast.show('저장했습니다'); modal.close();` 순서라, 지금 보면 곧 닫힐 모달이 아직
        // 열려 있어 토스트가 모달과 함께 사라진다. 같은 핸들러의 close() 가 끝난 뒤에 보면
        // 닫힌 모달은 빠지고(→ 화면 좌상단), 남는 모달만 잡힌다(→ 모달 헤더 좌측).
        // 반대로 방금 연 모달은 아직 is-open 전(최대 30ms)·등장 전이 중이라 헤더 좌표가 틀린다.
        // 그때는 전이(.22s)가 끝난 뒤에 그 모달 안에 띄운다.
        if (placeName === 'top-left') setTimeout(() => {
            if (this._modalOpening()) setTimeout(render, MODAL_SETTLE_MS);
            else render();
        }, 0);
        else render();
    };

    _render(msg, opts, { type, theme, placeName, timeout, body, closeBtn }) {
        // top-left 는 원래 페이지 좌상단(네비바 아래) 기준이다. 그런데 모달이 떠 있을 때는
        // 페이지 모서리가 모달에 가려/동떨어져 보인다 — 이때는 **열려 있는 모달의 헤더 좌측**에
        // 붙인다(페이지 좌표가 아니라 헤더 좌표). place 를 명시하지 않은 호출만 대상이라,
        // opts.place 로 위치를 직접 지정한 호출은 그대로 그 자리에 뜬다.
        const $modalHeader = placeName === 'top-left' ? this._modalHeader() : null;
        const place = placeName === 'bottom'   ? ' jb-toast--bottom'
                    : $modalHeader              ? ' jb-toast--modal'
                    : placeName === 'top-left' ? ' jb-toast--tl'
                    : placeName === 'center'   ? ' jb-toast--center'
                    : '';

        const $t = this._getEl();
        clearTimeout(this._timer);
        $t.attr('class', `jb-toast jb-toast--${theme} is-${type}${place}`)
          .css({ background: opts.bg || '', color: opts.color || '' })   // 호출별 색 지정(테마보다 우선)
          .css('--jb-toast-bottom', `${opts.offset || this._siteOffset()}px`)
          // top 은 CSS 기본(76px)을 쓰고, offset 을 준 호출만 변수로 덮는다.
          // 안 준 호출에서 앞 호출의 값이 새지 않도록 빈 값으로 지운다.
          .css('--jb-toast-top', opts.offset ? `${opts.offset}px` : '')
          .html(`<span class="jb-toast-ico"></span><span class="jb-toast-msg">${body}</span>${closeBtn}`);

        // 모달 헤더 기준 좌표는 인라인 style 로 얹는다(뷰포트 기준 — 모달도 position:fixed 라
        // getBoundingClientRect 가 그대로 맞는다). top-left 자리를 안 쓰는 호출에서 남은 값이
        // 다음 호출에 새지 않도록, 아닐 때는 비운다.
        // z-index 도 그 모달 바로 위로 — 토스트 기본(popover 1065)은 첫 모달(1055)만 넘는다.
        // 모달이 겹치면 Layer 가 한 겹마다 20씩 올려(1075…) 위 모달 뒤에 가려진다.
        if ($modalHeader) {
            const r = $modalHeader[0].getBoundingClientRect();
            const z = parseInt($modalHeader.closest('.ui-modal').css('z-index'), 10);
            $t.css({ top: `${r.top + 10}px`, left: `${r.left + 12}px`, zIndex: z > 0 ? z + 1 : '' });
        } else {
            $t.css({ top: '', left: '', zIndex: '' });
        }

        // 리플로우 후 열기(전이 발동)
        void $t[0].offsetHeight;
        $t.addClass('is-open');

        if (timeout) this._timer = setTimeout(this.hide, timeout);
    }

    /**
     * 이 화면의 공통 기본값을 정한다(페이지 모듈에서 1회).
     * show() 가 직접 받은 opts 가 더 세다.
     */
    defaults = (opts = {}) => { this._defaults = { ...this._defaults, ...opts }; };

    /** 텍스트 내용으로 연다(이스케이프). */
    text = (msg, opts = {}) => this.show(msg, { ...opts, html: false });

    hide = () => {
        this._seq++;
        clearTimeout(this._timer);
        if (this.$el) this.$el.addClass('is-out').removeClass('is-open');
    };

    /* ───────────────── internal ───────────────── */

    /** 사이트별 기본 테마 — Admin=adm(화면설정 토큰), 그 외(Mobile/Devil)=dark. */
    _siteTheme() {
        return this._pageType() === 'Admin' ? 'adm' : 'dark';
    }

    /**
     * 사이트별 기본 위치 — Mobile·MobileAdmin=bottom(맨하단), Admin=top-left, 그 외=top.
     * 관리자를 상단 중앙에서 좌측 상단으로 옮겼다: 표·모달 위로 겹쳐 뜨면서 방금 누른
     * 자리와 제목줄을 가렸다. 좌상단은 네비바 아래·사이드바 오른쪽의 빈 칸이다.
     */
    _sitePlace() {
        if (this._isMobileSite()) return 'bottom';
        return this._pageType() === 'Admin' ? 'top-left' : 'top';
    }

    /** 하단 배치의 기본 띄움 거리 — /ma/ 는 하단 네비(64px)를 피해 그 위에 얹는다. */
    _siteOffset() {
        return this._pageType() === 'MobileAdmin' ? 76 : 24;
    }

    /**
     * 현재 열려 있는 모달(공용 $j.Ui.Modal, .ui-modal.is-open)의 헤더.
     * 여러 모달이 겹쳐 있으면 가장 나중에 열린(=DOM 맨 뒤) 것 — Modal.open() 이 매번
     * body 맨 끝에 append 하므로 DOM 순서가 곧 스택 순서다. 없으면 null.
     */
    /** 열리는 중인 모달이 있나 — DOM 에 있는데 아직 is-open 이 없고, 닫히는 중(is-closing)도 아닌 것 */
    _modalOpening() {
        if (typeof document === 'undefined') return false;
        return !!document.querySelector('.ui-modal:not(.is-open):not(.is-closing)');
    }

    _modalHeader() {
        if (typeof document === 'undefined') return null;
        const modals = document.querySelectorAll('.ui-modal.is-open');
        if (!modals.length) return null;
        const modal = modals[modals.length - 1];
        return $(modal.querySelector('.ui-modal__header') || modal);
    }

    _isMobileSite() {
        const t = this._pageType();
        return t === 'Mobile' || t === 'MobileAdmin';
    }

    _pageType() {
        return (document.body && document.body.dataset.pageType) || '';
    }

    _getEl() {
        this._injectStyle();
        if (!this.$el) {
            this.$el = $('<div class="jb-toast"></div>').appendTo('body');
            // 닫기 버튼은 show() 마다 다시 그려지므로 위임으로 한 번만 묶는다
            this.$el.on('click', '.jb-toast-x', (e) => { e.preventDefault(); this.hide(); });
        }
        return this.$el;
    }

    _injectStyle() {
        if (this._styleInjected) return;
        this._styleInjected = true;
        const css = `
.jb-toast{position:fixed;top:-120px;left:50%;transform:translateX(-50%);
  z-index:var(--z-index-popover,1065);display:flex;align-items:center;gap:9px;
  width:86%;max-width:500px;padding:12px 18px;border-radius:14px;
  background:#222;color:#fff;font-size:14px;font-weight:600;line-height:1.5;
  box-shadow:0 10px 30px rgba(0,0,0,.20);opacity:0;
  transition:top .42s cubic-bezier(.22,1,.36,1),bottom .42s cubic-bezier(.22,1,.36,1),opacity .42s}
.jb-toast.is-open{top:calc(env(safe-area-inset-top,0px) + var(--jb-toast-top,76px));opacity:1}
/* 하단 배치(place:'bottom', 모바일 기본) — 맨하단에서 살짝 떠오르며 나타나고, 그 자리에서 스르르 사라진다 */
/* max-width 는 86%(=390px 폰에서 335px, 좌우 padding 44px 빼면 20자 남짓)로는 한 문장짜리
   안내가 두 줄로 접혔다. 94% 로 넓히고 좌우 여백을 줄여 한 줄이 들어가게 한다.
   상한(520px)은 태블릿·PC 에서 알약이 지나치게 길어지지 않게 잡은 것. */
.jb-toast--bottom{top:auto;bottom:calc(env(safe-area-inset-bottom,0px) + var(--jb-toast-bottom,24px));
  width:auto;max-width:min(94%,520px);padding:11px 18px;border-radius:24px;font-size:13px;
  transform:translate(-50%,14px);pointer-events:none;
  transition:transform .22s cubic-bezier(.22,1,.36,1),opacity .22s ease}
.jb-toast--bottom.is-open{top:auto;transform:translate(-50%,0);opacity:1}
/* 사라질 때만 느린 페이드(움직이지 않는다) — is-open 뒤에 선언해 우선 적용 */
.jb-toast--bottom.is-out{transform:translate(-50%,0);opacity:0;transition:opacity .6s ease}
/* 좌측 상단 배치(place:'top-left') — 화면 가운데를 비워야 하는 곳(관리자 편집창 등)의 짧은 알림 */
/* --adm-navh(네비바 높이)·--adm-sbw(사이드바 폭)는 $j.F.Admin.ChatPanel 이 실측해 body 에 넣는다.
   팝업/bare 레이아웃엔 둘 다 없어 0 이 되고, 그러면 화면 모서리에 그대로 붙는다.
   관리자 밖(모바일·악덕)에서는 변수가 아예 없어 역시 0 — 예전 8px 자리와 같다. */
.jb-toast--tl{left:calc(var(--adm-sbw,0px) + 12px);transform:none;width:auto;
  max-width:min(520px,70%);padding:9px 14px;border-radius:10px;font-size:13px}
.jb-toast--tl.is-open{top:calc(env(safe-area-inset-top,0px) + var(--adm-navh,0px) + 12px);opacity:1}
.jb-toast--tl .jb-toast-msg{text-align:left}
/* 모달 헤더 좌측 배치(place:'top-left' + 모달이 열려 있을 때 자동 전환) — 좌표는 JS 가
   모달 헤더의 getBoundingClientRect 로 인라인 top/left 를 얹는다(뷰포트 기준, 모달도
   position:fixed 라 그대로 맞는다). transform 은 base(.jb-toast) 의 translateX(-50%) 를
   지워야 좌측 정렬이 유지된다 — 지우지 않으면 헤더 좌측이 아니라 그 지점에서 자기 폭의
   절반만큼 더 왼쪽으로 밀린다. */
.jb-toast--modal{transform:none;width:auto;max-width:min(420px,80%);
  padding:9px 14px;border-radius:10px;font-size:13px}
.jb-toast--modal .jb-toast-msg{text-align:left}
/* 정중앙 배치(place:'center') — 슬라이드가 아니라 그 자리에서 옅게 확대되며 나타난다.
   opt-in 전용이라 사이트 기본값으로는 절대 선택되지 않는다(_sitePlace 참고). */
.jb-toast--center{top:50%;bottom:auto;transform:translate(-50%,-50%) scale(.92)}
.jb-toast--center.is-open{top:50%;transform:translate(-50%,-50%) scale(1);opacity:1}
.jb-toast--center.is-out{transform:translate(-50%,-50%) scale(1);opacity:0;transition:opacity .5s ease}
.jb-toast-msg{flex:1;text-align:center}
/* 닫기 버튼(opts.closable) — 오래 띄우는 알림을 사용자가 직접 치울 수 있게 */
.jb-toast-x{position:relative;flex:0 0 auto;width:26px;height:26px;margin:-4px -8px -4px 2px;padding:0;
  border:0;border-radius:50%;background:rgba(255,255,255,.18);color:inherit;
  font-size:13px;line-height:1;cursor:pointer;-webkit-appearance:none;
  pointer-events:auto} /* 하단 토스트는 pointer-events:none 이라 버튼만 되살린다 */
/* 보이는 원은 26px 그대로 두고 탭 영역만 44px 로 넓힌다 — 폰에서 26px 은 손가락으로 못 맞춘다.
   토스트가 pointer-events:none 이라 넓힌 영역이 뒤 버튼을 새로 가리는 범위는 이 44px 뿐이다. */
.jb-toast-x::after{content:'';position:absolute;inset:-9px}
.jb-toast-x:active{background:rgba(255,255,255,.32)}
.jb-toast--yellow .jb-toast-x{background:rgba(0,0,0,.12)}
.jb-toast--yellow .jb-toast-x:active{background:rgba(0,0,0,.22)}
/* 테마 — 사이트별 기본 또는 opts.theme. 자식까지 색 강제(리치 html 상속 누수 방지) */
.jb-toast--dark,.jb-toast--dark .jb-toast-msg,.jb-toast--dark .jb-toast-msg *{color:#fff}
.jb-toast--green,.jb-toast--green .jb-toast-msg,.jb-toast--green .jb-toast-msg *{color:#fff}
.jb-toast--yellow,.jb-toast--yellow .jb-toast-msg,.jb-toast--yellow .jb-toast-msg *{color:#111}
.jb-toast--dark{background:#222}
.jb-toast--green{background:#16a34a}
.jb-toast--yellow{background:#ffc72d}
/* 관리자 기본 테마 — 화면설정(다크·글자크기·부드럽게) 3축을 그대로 탄다.
   초록 알약이던 것을 관리자의 다른 면(카드·드롭다운)과 같은 판으로 바꿨다.
   ⚠ 처음엔 바탕을 중립으로 두고 왼쪽 3px 띠에만 색을 줬는데 **톤 구분이 안 됐다**.
   면 전체를 옅게 물들이고(tint) 글자·테두리까지 같은 색줄기로 맞춘다 —
   badge 계열(wash·tint·edge·solid·ink)의 짝은 관리자 전역에서 쓰는 그 규약이다.
   토큰이 없는 곳(모바일·악덕에서 theme:'adm' 을 직접 지정)을 위해 폴백을 함께 둔다. */
.jb-toast--adm{
  background:var(--adm-surface-raised,#fff);
  border:1px solid var(--adm-border,#dee2e6);
  border-left-width:4px;   /* 톤이 없을 때도 왼쪽만 굵게 — 알림이라는 신호 */
  border-left-color:var(--adm-border-strong,#adb5bd);
  border-radius:var(--adm-radius-md,6px);
  box-shadow:var(--adm-shadow-dropdown,0 6px 20px rgba(0,0,0,.14));
  font-size:13px}
.jb-toast--adm,.jb-toast--adm .jb-toast-msg,.jb-toast--adm .jb-toast-msg *{color:var(--adm-text,#212529)}
.jb-toast--adm .jb-toast-ico{background:var(--adm-n-hover,rgba(0,0,0,.08));color:var(--adm-text-secondary,#6c757d)}
.jb-toast--adm .jb-toast-x{background:transparent;color:inherit;opacity:.55}
.jb-toast--adm .jb-toast-x:active{background:var(--adm-n-hover,rgba(0,0,0,.12));opacity:1}
/* 톤 — 면·테두리·글자를 한 색줄기로. 램프에서 고른 단수는:
     면 edge(3단) · 테두리 solid(4단) · 글자 deep(6단) · 아이콘 solid 채움
   처음엔 면을 tint(2단)로 잡았는데 **흐리멍텅해서 톤이 안 갈렸다** — tint 는 본문 속에
   박히는 작은 뱃지용 채움이라, 본문 **위에** 떠서 1.5초 만에 읽혀야 하는 토스트엔 약하다.
   한 단 올려 edge 를 면으로 쓴다(테두리용 단수지만 램프 위에서 고른 값이라 계열은 그대로다).
   글자를 ink(5단) 대신 deep(6단)으로 내려 대비를 벌었다.

   ⚠ 면을 색 토큰 하나로 두면 안 된다. **다크에서 edge 는 rgba(색,.42) 라 반투명**이고,
   토스트는 본문 위에 뜨므로 글자가 비쳐 읽히지 않았다. 색을 그라데이션 레이어로 깔고
   그 밑에 불투명한 면(surface-raised)을 받친다 — 라이트/다크가 같은 코드로 불투명해진다. */
.jb-toast--adm.is-success,.jb-toast--adm.is-warning,
.jb-toast--adm.is-error,.jb-toast--adm.is-info{
  background:linear-gradient(var(--jb-tone),var(--jb-tone)),var(--adm-surface-raised,#fff)}
.jb-toast--adm.is-success{--jb-tone:var(--adm-edge-green,#86efac);border-color:var(--adm-solid-green,#22c55e)}
.jb-toast--adm.is-success,.jb-toast--adm.is-success .jb-toast-msg,.jb-toast--adm.is-success .jb-toast-msg *{color:var(--adm-deep-green,#14532d)}
.jb-toast--adm.is-success .jb-toast-ico{background:var(--adm-solid-green,#22c55e);color:var(--adm-solid-fg,#fff)}
.jb-toast--adm.is-warning{--jb-tone:var(--adm-edge-amber,#fde68a);border-color:var(--adm-solid-amber,#f59e0b)}
.jb-toast--adm.is-warning,.jb-toast--adm.is-warning .jb-toast-msg,.jb-toast--adm.is-warning .jb-toast-msg *{color:var(--adm-deep-amber,#78350f)}
.jb-toast--adm.is-warning .jb-toast-ico{background:var(--adm-solid-amber,#f59e0b);color:var(--adm-solid-fg,#fff)}
.jb-toast--adm.is-error{--jb-tone:var(--adm-edge-red,#fca5a5);border-color:var(--adm-solid-red,#dc2626)}
.jb-toast--adm.is-error,.jb-toast--adm.is-error .jb-toast-msg,.jb-toast--adm.is-error .jb-toast-msg *{color:var(--adm-deep-red,#7f1d1d)}
.jb-toast--adm.is-error .jb-toast-ico{background:var(--adm-solid-red,#dc2626);color:var(--adm-solid-fg,#fff)}
.jb-toast--adm.is-info{--jb-tone:var(--adm-edge-blue,#bfdbfe);border-color:var(--adm-solid-blue,#3b82f6)}
.jb-toast--adm.is-info,.jb-toast--adm.is-info .jb-toast-msg,.jb-toast--adm.is-info .jb-toast-msg *{color:var(--adm-deep-blue,#1e3a8a)}
.jb-toast--adm.is-info .jb-toast-ico{background:var(--adm-solid-blue,#3b82f6);color:var(--adm-solid-fg,#fff)}
/* 좌측 아이콘 배지 — 반투명이라 어떤 배경에서도 자연스럽게 얹힘 */
.jb-toast-ico{flex:0 0 auto;width:20px;height:20px;border-radius:50%;
  display:none;align-items:center;justify-content:center;font-size:13px;font-weight:800;
  background:rgba(255,255,255,.25);color:inherit}
.jb-toast--yellow .jb-toast-ico{background:rgba(0,0,0,.15)}
.jb-toast.is-success .jb-toast-ico,.jb-toast.is-warning .jb-toast-ico,
.jb-toast.is-error .jb-toast-ico,.jb-toast.is-info .jb-toast-ico{display:flex}
.jb-toast.is-success .jb-toast-ico::before{content:'✓'}
.jb-toast.is-warning .jb-toast-ico::before{content:'!'}
.jb-toast.is-error   .jb-toast-ico::before{content:'✕'}
.jb-toast.is-info    .jb-toast-ico::before{content:'i'}
`;
        $('<style>').text(css).appendTo('head');
    }

    _esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
}
